import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import {
  useKnowledgeGraphQuery,
  usePublishedKnowledgeBasesQuery,
} from '@/hooks/useKnowledgeGraphQuery'
import { domainColorScale } from '@/components/graph/graphPalette'
import { ForceGraph } from '@/components/graph/ForceGraph'
import { GraphDetailPanel } from '@/components/graph/GraphDetailPanel'
import { DataTable } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  CheckIcon,
  ChevronDownIcon,
  MenuIcon,
  SearchAltIcon,
} from '@/components/layout/icons'
import { shortDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useFoldable } from '@/hooks/useFoldable'
import type {
  KnowledgeGraphRef,
  PublishedKnowledgeBaseOption,
} from '@/lib/knowledgeBaseApi'
type KgView = 'graph' | 'table'

const VIEW_LABEL: Record<KgView, string> = { graph: 'Graph', table: 'Table' }

export function KnowledgeGraphRoutePending() {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <KnowledgeGraphViewSkeleton />
    </div>
  )
}

import { KnowledgeGraphViewSkeleton } from '@/components/graph/KnowledgeGraphViewSkeleton'
export { KnowledgeGraphViewSkeleton }

export interface KnowledgeGraphViewProps {
  initialKbId?: string | null
  fallbackKb?: PublishedKnowledgeBaseOption
  enabled?: boolean
  /** `draft` loads the open base with `ref=draft` (no published release). */
  graphRef?: KnowledgeGraphRef
}

export function KnowledgeGraphView({
  initialKbId,
  fallbackKb,
  enabled = true,
  graphRef = 'published',
}: KnowledgeGraphViewProps = {}) {
  const { isDualPane } = useFoldable()
  const published = usePublishedKnowledgeBasesQuery({ enabled })
  const bases = useMemo(() => {
    const raw = published.data ?? []
    if (initialKbId && !raw.some((base) => base.id === initialKbId)) {
      const label = fallbackKb?.label || initialKbId
      return [{ id: initialKbId, label }, ...raw]
    }
    return raw
  }, [published.data, initialKbId, fallbackKb])

  const [pickedId, setPickedId] = useState<string | null>(initialKbId ?? null)

  useEffect(() => {
    if (initialKbId) {
      setPickedId(initialKbId)
    }
  }, [initialKbId])

  const kbId =
    pickedId && bases.some((base) => base.id === pickedId)
      ? pickedId
      : (bases[0]?.id ?? null)
  const ref: KnowledgeGraphRef =
    graphRef === 'draft' && kbId != null && kbId === initialKbId
      ? 'draft'
      : 'published'
  const { data, isPending, isError, error, refetch, isFetching } =
    useKnowledgeGraphQuery(kbId, { enabled, ref })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeDomain, setActiveDomain] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [view, setView] = useState<KgView>('graph')
  const [graphReady, setGraphReady] = useState(false)

  const domainColor = useMemo(
    () => domainColorScale(data?.domains ?? []),
    [data?.domains],
  )
  const selectedNode = data?.nodes.find((n) => n.id === selectedId) ?? null

  const tableRows = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    return data.nodes
      .filter(
        (n) =>
          (!activeDomain || n.domain === activeDomain) &&
          (!q || n.label.toLowerCase().includes(q)),
      )
      .map((n) => ({
        name: n.label,
        domain: n.domain,
        kind: n.kind || '—',
        source: n.source_tables.join(', '),
        count: n.relationship_count,
        rows: n.rows_est,
      }))
  }, [data, activeDomain, search])

  useEffect(() => {
    setGraphReady(false)
    setSelectedId(null)
  }, [kbId, data?.nodes, data?.edges])

  const hasGraphNodes = (data?.nodes.length ?? 0) > 0
  const awaitingGraphLayout = view === 'graph' && hasGraphNodes && !graphReady
  const graphLoading = Boolean(kbId) && isPending && !data

  if (published.isPending && !published.data && !initialKbId) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <KnowledgeGraphViewSkeleton />
      </div>
    )
  }

  if (published.isError && bases.length === 0) {
    const message =
      published.error instanceof Error
        ? published.error.message
        : 'Could not load published knowledge bases.'
    return (
      <GraphLoadError
        title="Knowledge bases unavailable"
        message={message}
        onRetry={() => void published.refetch()}
        busy={published.isFetching}
      />
    )
  }

  const showPanel = view === 'graph' && selectedNode

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {awaitingGraphLayout && (
        <div className="bg-card absolute inset-0 z-20">
          <KnowledgeGraphViewSkeleton />
        </div>
      )}

      <header className="flex-none">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {/* Title & Badge block: Removed in folded view only */}
          {!isDualPane && (
            <div
              className={cn(
                'flex min-w-0 items-center gap-2.5',
                initialKbId && 'max-md:hidden',
              )}
            >
              {!initialKbId && (
                <button
                  type="button"
                  className="text-text-3 hover:text-text -ml-1 shrink-0 rounded-lg p-1.5 lg:hidden"
                  aria-label="Open navigation menu"
                  onClick={() => window.dispatchEvent(new Event('vision:open-nav'))}
                >
                  <MenuIcon className="h-5 w-5" />
                </button>
              )}
              <h2 className="text-ink max-md:hidden text-xl font-semibold tracking-tight lg:text-2xl">
                Knowledge Graph
              </h2>
              {data && (
                <span className="bg-wash text-text-2 max-md:hidden rounded-full px-2 py-0.5 font-mono text-[11px]">
                  {data.level}
                </span>
              )}
            </div>
          )}

          {/* Search entities and dropdown menu: on the left in folded view (or right-aligned in standard desktop) */}
          <div
            className={cn(
              'flex min-w-0 flex-nowrap items-center gap-2',
              isDualPane ? 'w-auto' : 'w-full md:ml-auto md:w-auto',
            )}
          >
            {isDualPane && !initialKbId && (
              <button
                type="button"
                className="text-text-3 hover:text-text -ml-1 shrink-0 rounded-lg p-1.5 flex"
                aria-label="Open navigation menu"
                onClick={() => window.dispatchEvent(new Event('vision:open-nav'))}
              >
                <MenuIcon className="h-5 w-5" />
              </button>
            )}
            <label className="border-border bg-card relative flex h-8 w-[150px] sm:w-[160px] max-w-full shrink-0 items-center rounded-full border">
              <SearchAltIcon className="text-ink-muted pointer-events-none absolute left-3 h-3.5 w-3.5" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search entities…"
                aria-label="Search entities"
                className="text-ink placeholder:text-ink-muted h-full w-full bg-transparent pr-3 pl-9 text-[13px] focus:outline-none"
              />
            </label>
            <PublishedKnowledgeBasePicker
              options={bases}
              value={kbId}
              onChange={setPickedId}
            />
            {!isDualPane && (
              <div
                role="group"
                aria-label="View"
                className="border-border-2 bg-card hidden h-8 shrink-0 overflow-hidden rounded-full border md:flex"
              >
                {(['graph', 'table'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={view === v}
                    onClick={() => setView(v)}
                    className={cn(
                      'h-full rounded-none px-3.5 text-[13px] font-medium transition-colors duration-150',
                      view === v
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text hover:bg-wash',
                    )}
                  >
                    {VIEW_LABEL[v]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right side in folded view: Graph | Table toggle */}
          {isDualPane && (
            <div
              role="group"
              aria-label="View"
              className="border-border-2 bg-card ml-auto flex h-8 shrink-0 overflow-hidden rounded-full border"
            >
              {(['graph', 'table'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                  className={cn(
                    'h-full rounded-none px-3.5 text-[13px] font-medium transition-colors duration-150',
                    view === v
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text hover:bg-wash',
                  )}
                >
                  {VIEW_LABEL[v]}
                </button>
              ))}
            </div>
          )}
        </div>

        {data && (
          <p className="text-ink-muted mt-1 text-xs">
            {data.node_count} entities · {data.edge_count} relationships ·
            updated {shortDate(data.generated_at)}
          </p>
        )}

        {data && data.domains.length > 0 && (
          <div className="mt-3 flex gap-2 max-md:flex-nowrap max-md:overflow-x-auto max-md:pb-0.5 md:flex-wrap">
            {data.domains.map((d) => {
              const active = activeDomain === d
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setActiveDomain(active ? null : d)}
                  title={data.category_descriptions[d]}
                  className={cn(
                    'border-border flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] transition-colors duration-150',
                    active
                      ? 'bg-wash-2 text-text border-transparent font-medium'
                      : 'text-text-2 hover:bg-wash',
                  )}
                >
                  <span
                    aria-hidden
                    className="size-2 flex-none rounded-full"
                    style={{
                      background: active ? domainColor(d) : 'transparent',
                      borderColor: domainColor(d),
                      borderWidth: active ? 0 : 1.5,
                      borderStyle: 'solid',
                    }}
                  />
                  {d}
                </button>
              )
            })}
          </div>
        )}
      </header>

      <div className="border-border relative mt-3 flex min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border">
        {graphLoading ? (
          <Skeleton className="min-h-[24rem] flex-1 rounded-none" />
        ) : isError || !data ? (
          <GraphLoadError
            title="Knowledge Graph unavailable"
            message={
              !kbId
                ? 'No published knowledge bases to graph.'
                : error instanceof Error
                  ? error.message
                  : 'Could not load the knowledge graph.'
            }
            onRetry={kbId ? () => void refetch() : undefined}
            busy={isFetching}
          />
        ) : (
          <div
            className={cn(
              'bg-card min-h-[24rem] min-w-0 flex-1 overflow-hidden',
              view === 'table' && 'overflow-y-auto',
            )}
          >
            {data.nodes.length === 0 ? (
              <EmptyGraph />
            ) : (
              <>
                <div
                  className={cn(
                    'h-full min-h-[24rem]',
                    view !== 'graph' && 'max-md:block md:hidden',
                  )}
                >
                  <ForceGraph
                    nodes={data.nodes}
                    edges={data.edges}
                    domainColor={domainColor}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    activeDomain={activeDomain}
                    searchQuery={search}
                    onReadyChange={setGraphReady}
                    showLayoutStatus={!awaitingGraphLayout}
                  />
                </div>
                {view === 'table' && (
                  <div className="hidden p-4 md:block">
                    {tableRows.length === 0 ? (
                      <p className="text-ink-muted text-sm">
                        No entities match the current filters.
                      </p>
                    ) : (
                      <DataTable
                        caption="Knowledge graph entities"
                        columns={[
                          { key: 'name', label: 'Name' },
                          { key: 'domain', label: 'Domain' },
                          { key: 'kind', label: 'Kind' },
                          { key: 'source', label: 'Source tables' },
                          {
                            key: 'count',
                            label: 'Relationships',
                            numeric: true,
                          },
                          { key: 'rows', label: 'Rows (est)', numeric: true },
                        ]}
                        rows={tableRows}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {showPanel && data && selectedNode && (
          <>
            <button
              type="button"
              aria-label="Close panel"
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-xs lg:hidden"
              onClick={() => setSelectedId(null)}
            />
            <div className="border-border bg-card shadow-float fixed inset-y-0 right-0 z-50 flex w-[min(100%,340px)] flex-col rounded-md border lg:relative lg:z-auto lg:w-auto lg:rounded-none lg:border-t-0 lg:border-r-0 lg:border-b-0 lg:shadow-none">
              <GraphDetailPanel
                node={selectedNode}
                nodes={data.nodes}
                edges={data.edges}
                domainColor={domainColor}
                onSelectNode={setSelectedId}
                onClose={() => setSelectedId(null)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function PublishedKnowledgeBasePicker({
  options,
  value,
  onChange,
}: {
  options: PublishedKnowledgeBaseOption[]
  value: string | null
  onChange: (id: string) => void
}) {
  const selected = options.find((option) => option.id === value)
  const label = selected?.label ?? 'Knowledge base'
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Knowledge base"
          aria-haspopup="menu"
          disabled={options.length === 0}
          className="border-border bg-card text-ink hover:bg-wash inline-flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-full border px-3 text-[13px] disabled:opacity-60 md:w-auto md:max-w-[320px] md:min-w-[12rem] md:flex-none"
        >
          <span className="truncate">
            {options.length === 0 ? 'No knowledge bases' : label}
          </span>
          <ChevronDownIcon className="text-ink-muted shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={12}
        className="w-[min(320px,calc(100vw-1.5rem))]"
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            role="menuitemradio"
            aria-checked={option.id === value}
            onSelect={() => onChange(option.id)}
          >
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
            <CheckIcon
              aria-hidden
              size={16}
              className={cn('text-primary', option.id !== value && 'invisible')}
            />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function GraphLoadError({
  title,
  message,
  onRetry,
  busy,
}: {
  title: string
  message: string
  onRetry?: () => void
  busy?: boolean
}) {
  return (
    <div className="flex min-h-[24rem] flex-1 flex-col items-center justify-center p-6 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400">
        <AlertTriangle className="size-5" />
      </div>
      <h2 className="text-text text-base font-semibold">{title}</h2>
      <p className="text-text-3 mt-1 max-w-md text-sm">{message}</p>
      {onRetry && (
        <Button
          type="button"
          variant="outline"
          onClick={onRetry}
          className="mt-4 rounded-xl"
          disabled={busy}
        >
          <RefreshCw className="size-4" />
          Try again
        </Button>
      )}
    </div>
  )
}

function EmptyGraph() {
  return (
    <div className="flex h-full min-h-[24rem] flex-col items-center justify-center p-8 text-center">
      <h3 className="text-ink text-base font-semibold">No entities</h3>
      <p className="text-ink-muted mt-1 max-w-sm text-sm">
        The knowledge graph response did not include any nodes.
      </p>
    </div>
  )
}
